import React, { Component } from "react";
import CookieConsent from "react-cookie-consent";
import SEO from "react-seo-component";
import { Col, Container, Row } from "reactstrap";
import ContactUs from "../../components/common/ContactUs";

/**
 * QikTruck Privacy Policy (South Africa, POPIA-aligned) - Website + Apps
 * Source file reviewed and modernised: :contentReference[oaicite:0]{index=0}
 *
 * Notes:
 * - This is a practical POPIA-first draft for a logistics marketplace.
 * - You should still have local counsel review final wording, especially around cross-border transfers,
 *   direct marketing rules, and any insurer/claims requirements.
 */
class Section extends Component {
  render() {
    const lastUpdated = "January 01, 2026";

    return (
      <React.Fragment>
        <SEO
          title={"QikTruck Privacy Policy - POPIA compliant data handling for bookings and deliveries."}
          titleTemplate={"QikTruck"}
          titleSeparator={"-"}
          description={
            "Read QikTruck's Privacy Policy. Learn what personal information we collect for bookings, deliveries, driver onboarding and support, how we use it, how we share it, and your POPIA rights."
          }
          image={"https://media.qiktruck.co.za/qiktruck-preview-header.png"}
          pathname={"https://qiktruck.co.za/privacy"}
          twitterUsername={"qiktrucksa"}
        />

        <section id="home">
          <Container>
            <Row className="justify-content-center">
              <Col>
                <div className="hero-wrapper mb-4">
                  <div className="page">
                    <div className="translations-content-container">
                      <div className="container">
                        <div className="tab-content translations-content-item en visible" id="en">
                          <h3>Privacy Policy</h3>
                          <p>Last updated: {lastUpdated}</p>

                          <p>
                            This Privacy Policy explains how QikTruck ("we", "us", "our") collects, uses, shares,
                            stores, and protects personal information when you use our website, mobile applications,
                            and related services (collectively, the "Platform"). It also explains your rights under
                            the Protection of Personal Information Act 4 of 2013 ("POPIA") and how to exercise them.
                          </p>

                          <h3>1. Who we are</h3>
                          <p>
                            QikTruck operates a technology platform that connects customers with independent transport
                            providers (drivers) for deliveries, removals, and related transport services.
                          </p>

                          <h3>2. Key definitions</h3>
                          <ul>
                            <li>
                              <p>
                                <strong>Personal information</strong> has the meaning given in POPIA and includes
                                information that identifies you or can reasonably be linked to you.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Customer</strong> means a person or entity that requests or receives a service
                                through the Platform.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Driver</strong> means an independent transport provider who accepts jobs through
                                the Platform.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Operator</strong> means a third party who processes personal information for us,
                                under our instructions (for example, cloud hosting and analytics providers).
                              </p>
                            </li>
                          </ul>

                          <h3>3. What personal information we collect</h3>

                          <h4>3.1 Information you provide</h4>
                          <ul>
                            <li>
                              <p>
                                <strong>Account and contact details:</strong> name, email address, phone number, and
                                login credentials.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Booking details:</strong> pickup and delivery addresses, notes, item
                                descriptions, access requirements, delivery instructions, and contact details for
                                senders/recipients where provided by you.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Support communications:</strong> messages, emails, call notes, and complaints or
                                dispute information you submit.
                              </p>
                            </li>
                          </ul>

                          <h4>3.2 Information we collect automatically</h4>
                          <ul>
                            <li>
                              <p>
                                <strong>Device and usage data:</strong> IP address, browser type, device identifiers,
                                app version, pages/screens visited, and interaction logs.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Approximate location data:</strong> derived from IP or device settings where
                                enabled.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Cookies and similar technologies:</strong> for essential functionality and, if
                                you consent, analytics and marketing.
                              </p>
                            </li>
                          </ul>

                          <h4>3.3 Logistics-specific data</h4>
                          <p>
                            Because we operate a logistics marketplace, the Platform may also process the following
                            categories when relevant to a booking, safety, fraud prevention, or compliance:
                          </p>
                          <ul>
                            <li>
                              <p>
                                <strong>Live location tracking:</strong> driver GPS location during a job (where enabled)
                                to support dispatch, routing, ETAs, and safety.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Proof of collection/delivery:</strong> signatures, OTP confirmations, names, and
                                photos of delivered items or delivery locations where required by the service.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Incident and claims data:</strong> photos and descriptions of damaged goods,
                                accidents, or disputes.
                              </p>
                            </li>
                          </ul>

                          <h4>3.4 Driver onboarding information</h4>
                          <p>
                            If you register as a Driver (or submit information for Driver onboarding), we may process
                            additional information such as identity details, licence information, vehicle registration
                            details, compliance documents, bank details for payouts, and related verification outcomes.
                          </p>

                          <h3>4. Why we process personal information</h3>
                          <p>We process personal information for the following purposes:</p>
                          <ul>
                            <li>
                              <p>
                                <strong>Provide the Platform and perform bookings:</strong> create accounts, match
                                Customers and Drivers, manage bookings, provide ETAs, and facilitate delivery.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Payments and payouts:</strong> process payments, prevent fraud, issue receipts,
                                handle refunds, and facilitate Driver payouts where applicable.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Support and dispute handling:</strong> resolve queries, investigate incidents,
                                manage claims, and improve service quality.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Safety and security:</strong> prevent misuse, enforce terms, detect fraud, and
                                protect users and the Platform.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Legal and regulatory compliance:</strong> comply with applicable laws, respond
                                to lawful requests, and maintain records where required.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Analytics and improvement:</strong> understand usage patterns and improve the
                                Platform (subject to cookie preferences where applicable).
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Direct marketing:</strong> send service updates and promotions where permitted,
                                and allow you to opt out at any time.
                              </p>
                            </li>
                          </ul>

                          <h3>5. POPIA lawful grounds</h3>
                          <p>
                            We process personal information on one or more lawful grounds, depending on the context,
                            including:
                          </p>
                          <ul>
                            <li>
                              <p>
                                <strong>Performance of a contract:</strong> to provide bookings and services you request.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Legitimate interests:</strong> to operate, secure, and improve the Platform,
                                prevent fraud, and support service quality, balanced against your rights.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Legal obligation:</strong> where processing is required by law.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Consent:</strong> where we request it, such as certain cookies and marketing in
                                specific contexts.
                              </p>
                            </li>
                          </ul>

                          <h3>6. How we share personal information</h3>
                          <p>We may share personal information in limited circumstances, including:</p>
                          <ul>
                            <li>
                              <p>
                                <strong>With Drivers:</strong> to fulfil a booking (for example, pickup/delivery
                                addresses, contact details, and instructions).
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>With Operators and service providers:</strong> such as hosting, analytics,
                                customer support tooling, mapping, communications, and payment processing, under
                                appropriate safeguards and agreements.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>With business partners:</strong> only where necessary for a specific service or
                                integration, and subject to confidentiality and purpose limitation.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>For legal reasons:</strong> to comply with law, enforce our terms, or protect
                                rights, safety, and security.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Business transfers:</strong> if the business is restructured, sold, merged, or
                                similar, subject to continued protection of your information.
                              </p>
                            </li>
                          </ul>

                          <p>
                            We do not sell personal information as a business model. If this changes, we will update
                            this policy and, where required, obtain appropriate consent.
                          </p>

                          <h3>7. Cross-border transfers</h3>
                          <p>
                            We may store or process personal information in South Africa and, in some cases, in other
                            countries where our Operators operate (for example, cloud hosting). Where personal
                            information is transferred outside South Africa, we take reasonable steps to ensure
                            appropriate safeguards consistent with POPIA, including contractual protections and risk
                            assessments where appropriate.
                          </p>

                          <h3>8. Retention</h3>
                          <p>
                            We retain personal information only for as long as necessary for the purposes described in
                            this policy, unless a longer period is required or permitted by law. Typical retention
                            periods may include:
                          </p>
                          <ul>
                            <li>
                              <p>
                                <strong>Booking records:</strong> generally retained for audit, dispute, and operational
                                purposes for a reasonable period (commonly 3 to 5 years), unless law requires longer.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Support tickets and disputes:</strong> retained for a reasonable period after
                                resolution to manage repeat issues and compliance.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Marketing preferences:</strong> retained until you opt out or we no longer use
                                them.
                              </p>
                            </li>
                          </ul>

                          <h3>9. Security</h3>
                          <p>
                            We implement reasonable technical and organisational safeguards to protect personal
                            information against loss, unauthorised access, misuse, alteration, or destruction. No method
                            of transmission or storage is 100% secure, but we continuously review and improve controls.
                          </p>

                          <h3>10. Data incidents and breach response</h3>
                          <p>
                            If we become aware of a security compromise that creates a real risk of harm, we will take
                            reasonable steps to investigate, mitigate, and, where required by law, notify affected data
                            subjects and/or the Information Regulator.
                          </p>

                          <h3>11. Your POPIA rights</h3>
                          <p>
                            Subject to applicable law, you may have rights to request access to, correction of, or
                            deletion of personal information, object to certain processing, and withdraw consent where
                            processing is based on consent. You may also object to direct marketing at any time.
                          </p>
                          <p>
                            To exercise these rights, contact us using the details below and include enough information
                            for us to verify your identity and process the request.
                          </p>

                          <h3>12. Direct marketing</h3>
                          <p>
                            You can opt out of marketing communications at any time by using the unsubscribe link (where
                            provided) or by contacting us. Service and transactional messages (for example, booking
                            confirmations and delivery updates) are not marketing and may still be sent when necessary
                            to provide the service.
                          </p>

                          <h3>13. Children</h3>
                          <p>
                            The Platform is intended for adults and is not directed to children. If you believe a minor
                            has provided personal information to us without appropriate authority, contact us and we
                            will take reasonable steps to investigate and address the request.
                          </p>

                          <h3>14. Cookies and tracking</h3>
                          <p>
                            We use cookies and similar technologies for essential functionality. Where required, we ask
                            for consent for analytics and marketing cookies. You can also control cookies via your
                            browser settings, but disabling essential cookies may impact site functionality.
                          </p>

                          <h3>15. Changes to this Privacy Policy</h3>
                          <p>
                            We may update this Privacy Policy from time to time. If changes are material, we will take
                            reasonable steps to notify you through the Platform or other appropriate channels. The
                            updated policy becomes effective when posted.
                          </p>

                          <h3>16. Contact us and Information Officer</h3>
                          <p>
                            If you have questions, concerns, or requests relating to privacy, POPIA rights, or this
                            policy, contact us below. Your request may be handled by our Information Officer or an
                            authorised delegate.
                          </p>
                          <ContactUs />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>

          <CookieConsent
            location="bottom"
            buttonText="Accept"
            declineButtonText="Decline"
            cookieName="qiktruck_cookie_consent"
            enableDeclineButton
            style={{ background: "#48494a" }}
            buttonStyle={{
              color: "white",
              background: "#4f6367",
              fontSize: "13px",
            }}
            declineButtonStyle={{
              color: "white",
              background: "#c95a5a",
              fontSize: "13px",
            }}
            expires={180}
          >
            <span style={{ fontSize: "10px" }}>
              We use cookies for essential site functionality. With your consent, we also use cookies for analytics and
              to improve your experience.{" "}
              <a href="/privacy" style={{ color: "white", textDecoration: "underline" }}>
                Learn more
              </a>
              .
            </span>
          </CookieConsent>
        </section>
      </React.Fragment>
    );
  }
}

export default Section;
